import { RouteRecordRaw } from 'vue-router';
import { PartialOptions as OverlayScrollbarsOptions } from 'overlayscrollbars';
import { ModelType } from '@llws/platform-shared-types/types/db/auth';
import { ISelectOption, TAccessType } from '..';
import main from '@/main';

export interface IRouteConfigAuthPermissionRedirect {
  permission: string;
  redirectTo: RouteRecordRaw;
}

// eslint-disable-next-line no-shadow
export enum ERouteMetaPermissionMode {
  /* Check user has access to a module for all modelIds, or -1, or the modelId provided. We assume ALL by default */
  ALL = 'ALL',
  /* Check user has access to a module for at least one modelId (no modelId value needs to be provided) */
  AT_LEAST_ONE = 'AT_LEAST_ONE'
}

export interface IRouteMetaPermissionConfig {
  key: string;
  keyType: keyof typeof ModelType;
  accessType?: TAccessType;
  idParam?: string;
  idVal?: string | number;
  mode?: ERouteMetaPermissionMode;
  // mode and checkAtleastOneModel look like doing same thing
  checkAtLeastOneModel?: boolean;
}

export interface IRouteConfigAuthMeta {
  required?: boolean;
  permissions?: string[] | IRouteMetaPermissionConfig[];
  isDynamicPermitted?: () => boolean;
  permissionRedirects?: IRouteConfigAuthPermissionRedirect[];
  condition?: 'and' | 'or';
}

export interface IRouteMetaFooterExtraButton {
  buttonText: string | (() => string),
  tooltip?: string,
  buttonProps?: Record<string, any>,
  buttonHandler?: () => void,
}

export interface IRouteMetaFooterBar {
  enabled: boolean;
  hasSave?: boolean;
  hasDelete?: boolean;
  hasTranslations?: boolean;
  hasTranslationsBtn?(): boolean;
  languageOptions?: () => ISelectOption[];
  hasBottomText?: boolean;
  hasLastUpdated?: boolean;
  hasCustomText?: boolean;
  hasCheckBox?: boolean;
  extraButtons?: Record<string, IRouteMetaFooterExtraButton>,
}

export interface IBreadcrumbConfig {
  to: string;
  title: string;
  disableTruncation?: boolean;
  disabled?: boolean;
}

export interface IFrameNavAccessConfig {
  key: string | string[];
  keyType: keyof typeof ModelType;
  accessType: TAccessType;
  idParam?: string;
}

export type IFrameNavTab = {
  label: string;
  labelKey?: string;
  route: string;
  activePath?: string | string[]; // Optional URL substring(s) to force-active this tab
  access: IFrameNavAccessConfig;
  children?: Omit<IFrameNavTab, 'children'>[];
};

export type IFrameNav = {
  homeRoute?: string;
  homeIcon?: string;
  includeTitle?: boolean;
  tabs: IFrameNavTab[];
};

export interface IRouteConfigMeta {
  title: string;
  titleKey?: string;
  subtitle?: string;
  subtitleKey?: string;
  dynamicTitle?(): string;
  auth: IRouteConfigAuthMeta;
  pageTitle?: string;
  active?: boolean;
  hideFromNav?: boolean;
  dynamicHideFromNav?(): boolean | undefined;
  disabled?(): { tooltip: string } | undefined;
  addNewLabel?: boolean;
  icon?: string;
  customIcon?: string;
  noPagePadding?: boolean;
  useContentBg?: boolean;
  hasOwnScrollbar?: boolean;
  hideBlade?: boolean;
  hidePageTitle?: boolean;
  children?: Array<RouteRecordRaw>
  parent?: RouteRecordRaw | null;
  backButtonTarget?: () => { isCustom: true, handler: () => void } | { isCustom: false, target: RouteRecordRaw } | null;
  footerBar?: IRouteMetaFooterBar;
  accordionRoutes?: Array<RouteRecordRaw>;
  badge?: { variant: string; text: string; };
  reportingUrl?: string;
  infoUrl?: string;
  iframeNav?: IFrameNav;
  overlayScrollbarsOptions?: OverlayScrollbarsOptions;
  breadcrumbs?: (app: Required<ReturnType<typeof main>>, disabled?: boolean) => IBreadcrumbConfig[];
  iframeBackButtonTarget?: () => RouteRecordRaw;
  hideIframeBackButton?: boolean,
  hasIframeBackButton?: boolean;
}
